import { Button, Card, CardBody, CardFooter, CardHeader, CardTitle, Col, Row } from "reactstrap";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import sidebar from "../../utils/sidebar";
import { useEffect, useMemo, useState } from "react";
import { IoCartOutline, IoCashOutline, IoCreateOutline, IoCubeOutline, IoWalletOutline } from "react-icons/io5";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { get } from "../../../../utils/helpers/api";
import { printOrderStatus } from "../../utils/helpers";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../../context/settings";

function fmt(n) {
  return parseFloat(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function StatCard({ icon, iconBg, value, label }) {
  return (
    <Card className="h-100">
      <CardBody className="d-flex align-items-start gap-3">
        <div className="sales-stat-icon" style={{ background: iconBg }}>
          {icon}
        </div>
        <div>
          <div className="sales-stat-value">{value}</div>
          <div className="sales-stat-label">{label}</div>
        </div>
      </CardBody>
    </Card>
  );
}

function ViewSupplierPage() {
  const { t } = useTranslation();
  const { formatDate, settings } = useSettings();
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [supplierData, setSupplierData] = useState(state?.supplierData || {});
  const [orders, setOrders] = useState([]);
  const [ordersMeta, setOrdersMeta] = useState({ total: 0, last_page: 1 });

  useEffect(() => {
    if (id) {
      getSupplier();
      getOrders();
    }
  }, [id]);

  const getSupplier = async () => {
    const data = await get('/purchases/suppliers/view/' + id);
    if (data?.data) setSupplierData(data.data);
  };

  const getOrders = async () => {
    const data = await get('/purchases/suppliers/orders/' + id);
    if (data?.orders) {
      setOrders(data.orders.data ?? []);
      setOrdersMeta({ total: data.orders.total, last_page: data.orders.last_page });
    }
  };

  const totalSpent = useMemo(
    () => orders.reduce((sum, o) => sum + parseFloat(o.total_amount ?? 0), 0),
    [orders]
  );

  const productsSummary = useMemo(() => {
    const map = {};
    orders.flatMap(o => o.items || []).forEach(item => {
      const key = item.product_name || 'Unknown';
      if (!map[key]) map[key] = { name: key, qty: 0, total: 0 };
      map[key].qty += parseFloat(item.quantity ?? 0);
      map[key].total += parseFloat(item.total_price ?? 0);
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [orders]);

  const supplierName = supplierData.company_name ||
    [supplierData.firstname, supplierData.lastname].filter(Boolean).join(' ') ||
    t('purchases.supplier');

  const initials = supplierData.company_name
    ? supplierData.company_name.slice(0, 2).toUpperCase()
    : [supplierData.firstname, supplierData.lastname]
        .filter(Boolean).map(n => n.charAt(0).toUpperCase()).join('');

  const location = [supplierData.city, supplierData.country].filter(Boolean).join(', ');

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          title={supplierName}
          onBack={() => navigate('/purchases/suppliers')}
        >
          <Button
            size="sm"
            outline
            color="secondary"
            className="d-flex align-items-center gap-1"
            onClick={() => navigate('/purchases/suppliers/edit/' + id, { state: { supplierData } })}
          >
            <IoCreateOutline size={14} />
            {t('common.edit')}
          </Button>
        </PageSubHeader>

        <div className="subheader-page-content scrollable">
          {/* Profile Card */}
          <Card className="mb-4">
            <CardBody>
              <div className="d-flex align-items-center gap-4">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                  style={{ width: 64, height: 64, background: '#e6faf5', fontSize: 22, fontWeight: 700, color: '#20c997' }}
                >
                  {initials}
                </div>
                <div>
                  <h5 className="mb-1 fw-bold">{supplierName}</h5>
                  <div className="d-flex flex-wrap gap-3 text-muted" style={{ fontSize: '0.85rem' }}>
                    {supplierData.email && <span>{supplierData.email}</span>}
                    {supplierData.phone_number && (
                      <span>{[supplierData.country_code, supplierData.phone_number].filter(Boolean).join(' ')}</span>
                    )}
                    {location && <span>{location}</span>}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Stats */}
          <Row className="g-3 mb-4">
            <Col lg="3" md="6">
              <StatCard
                icon={<IoWalletOutline size={18} color="#20c997" />}
                iconBg="#e6faf5"
                value={`${fmt(totalSpent)} ${settings?.sales?.base_currency ?? ''}`}
                label={t("common.totalAmount")}
              />
            </Col>
            <Col lg="3" md="6">
              <StatCard
                icon={<IoCartOutline size={18} color="#6C63FF" />}
                iconBg="#f0eeff"
                value={ordersMeta.total}
                label={t("purchases.orders")}
              />
            </Col>
            <Col lg="3" md="6">
              <StatCard
                icon={<IoCubeOutline size={18} color="#fd7e14" />}
                iconBg="#fff3e6"
                value={productsSummary.length}
                label={t("purchases.products")}
              />
            </Col>
            <Col lg="3" md="6">
              <StatCard
                icon={<IoCashOutline size={18} color="#e2214b" />}
                iconBg="#ffe6eb"
                value={supplierData.outstanding_balance + ' ' + (settings?.sales?.base_currency ?? '')}
                label={t("accounting.outstandingBalances")}
              />
            </Col>
          </Row>

          {/* Purchase Orders */}
          <Card className="mb-4">
            <CardHeader className="bg-white">
              <CardTitle tag="h6" className="mb-0 fw-bold">{t("purchases.orders")}</CardTitle>
            </CardHeader>
            {orders.length === 0 ? (
              <CardBody>
                <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>{t("purchases.noOrdersFound")}</p>
              </CardBody>
            ) : (
              <>
                {orders.map((order, i) => (
                  <div
                    key={order.id}
                    className="sales-panel-item d-flex align-items-center gap-3 px-3 py-2"
                    style={{ borderTop: i === 0 ? '1px solid #f0f0f0' : undefined, cursor: 'pointer' }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center rounded flex-shrink-0"
                      style={{ width: 36, height: 36, background: '#f0eeff', fontSize: '0.7rem', fontWeight: 700, color: '#6C63FF' }}
                    >
                      {i + 1}
                    </div>
                    <div className="flex-grow-1 overflow-hidden">
                      <div className="fw-semibold text-truncate" style={{ fontSize: '0.84rem' }}>
                        {order.order_number}
                      </div>
                      <div className="text-muted text-truncate" style={{ fontSize: '0.75rem' }}>
                        {formatDate(order.order_date) || '-'} &middot; {t('common.itemsCount', { count: order.items?.length ?? 0 })}
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                      {printOrderStatus(order.status)}
                      <span className="fw-bold" style={{ fontSize: '0.84rem' }}>${fmt(order.total_amount)}</span>
                    </div>
                  </div>
                ))}
                {ordersMeta.last_page > 1 && (
                  <CardFooter className="bg-white text-center py-2">
                    <Button color="link" size="sm" className="text-muted" disabled>
                      {t('purchases.viewAllOrders', { count: ordersMeta.total })}
                    </Button>
                  </CardFooter>
                )}
              </>
            )}
          </Card>

          {/* Products Purchased */}
          <Card className="mb-4">
            <CardHeader className="bg-white">
              <CardTitle tag="h6" className="mb-0 fw-bold">{t("purchases.products")}</CardTitle>
            </CardHeader>
            {productsSummary.length === 0 ? (
              <CardBody>
                <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>{t("inventory.noProductsFound")}</p>
              </CardBody>
            ) : (
              productsSummary.map((p, i) => (
                <div
                  key={i}
                  className="sales-panel-item d-flex align-items-center gap-3 px-3 py-2"
                  style={{ borderTop: i === 0 ? '1px solid #f0f0f0' : undefined }}
                >
                  <div
                    className="d-flex align-items-center justify-content-center rounded flex-shrink-0"
                    style={{ width: 36, height: 36, background: '#fff3e6', fontSize: '0.7rem', fontWeight: 700, color: '#fd7e14' }}
                  >
                    {i + 1}
                  </div>
                  <div className="flex-grow-1 overflow-hidden">
                    <div className="fw-semibold text-truncate" style={{ fontSize: '0.84rem' }}>{p.name}</div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>{t('common.qty')}: {p.qty}</div>
                  </div>
                  <span className="fw-bold flex-shrink-0" style={{ fontSize: '0.84rem' }}>${fmt(p.total)}</span>
                </div>
              ))
            )}
          </Card>
        </div>
      </div>
    </SidebarLayout>
  );
}

export default ViewSupplierPage;
